import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  CContainer, CRow, CCol, CCard, CCardBody, CCardHeader, 
  CAvatar, CBadge, CButton, CSpinner, CFormInput, CFormLabel, 
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter
} from '@coreui/react';
import { CreditCard, LogOut, Clock, Edit3, ArrowUpRight, CheckCircle2, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosInstance';

// Color Mapping for Tiers
const TIER_CONFIG = {
  Alpha: { color: '#FF5722', accentBg: 'rgba(255, 87, 34, 0.08)' },
  Beeta: { color: '#10B981', accentBg: 'rgba(16, 185, 129, 0.08)' },
  Gamma: { color: '#3B82F6', accentBg: 'rgba(59, 130, 246, 0.08)' },
  Delta: { color: '#A855F7', accentBg: 'rgba(168, 85, 247, 0.08)' },
  Sigma: { color: '#F59E0B', accentBg: 'rgba(245, 158, 11, 0.08)' },
};

// Fallback Colors in case Tier Name is unknown or custom
const FALLBACK_COLORS = [
  { color: '#3B82F6', accentBg: 'rgba(59, 130, 246, 0.08)' },
  { color: '#10B981', accentBg: 'rgba(16, 185, 129, 0.08)' },
  { color: '#A855F7', accentBg: 'rgba(168, 85, 247, 0.08)' },
  { color: '#F59E0B', accentBg: 'rgba(245, 158, 11, 0.08)' }
];

const UserProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);

  // Profile Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ fullName: '', email: '' });
  const [saving, setSaving] = useState(false);

  // Upgrade Plan Modal States
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [upgrading, setUpgrading] = useState(false);

  const memberId = localStorage.getItem('ID') || 1;

useEffect(() => {
  if (location.state?.openUpgradeModal || location.state?.openPlansModal) {
    navigate('/plans');
  }
}, [location, navigate]);
  // Helper to resolve Tier Name safely
  const getTierName = (tier) => {
    return tier?.tierName || tier?.TierName || tier?.sectionName || tier?.SectionName || 'Standard Plan';
  };

  // Dynamic Tier Style Resolver
  const getTierStyle = (tier, idx = 0) => {
    const tName = getTierName(tier);
    if (TIER_CONFIG[tName]) return TIER_CONFIG[tName];
    return FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
  };

  // Helper Function for Plan Validity Display Logic
  const getValidityDisplay = (tier) => {
    const expiry = tier?.expiryDate || tier?.ExpiryDate;
    if (expiry) {
      const formattedDate = new Date(expiry).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
      return `Till ${formattedDate}`;
    }

    const duration = tier?.durationInDays ?? tier?.DurationInDays;
    if (duration !== null && duration > 0) {
      return `${duration} Days`;
    }

    return 'Lifetime';
  };

  // Category Renderer Helper
  const renderCategoriesDisplay = (item) => {
    if (!item) return null;

    const titles = item.categoryTitles || item.CategoryTitles;

    if (Array.isArray(titles) && titles.length > 0) {
      return (
        <div className="d-flex flex-wrap gap-1 align-items-center my-1.5">
          {titles.map((cat, idx) => (
            <CBadge 
              key={idx} 
              color="info" 
              variant="outline" 
              className="px-1.5 py-0.5 fw-normal" 
              style={{ fontSize: '10.5px' }}
            >
              {typeof cat === 'object' ? cat.name || cat.categoryName : cat}
            </CBadge>
          ))}
        </div>
      );
    }

    if (typeof titles === 'string' && titles.trim() !== '') {
      return (
        <div className="d-flex align-items-center gap-1 my-1.5">
          <BookOpen size={12} className="text-muted flex-shrink-0" />
          <span className="text-secondary fw-semibold" style={{ fontSize: '11px', lineHeight: '1.3' }}>
            {titles}
          </span>
        </div>
      );
    }

    return null;
  };

  // Fetch Profile & Subscriptions
  const fetchProfileAndSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const [profileRes, subRes] = await Promise.allSettled([
        api.get(`/api/members/profile/${memberId}`),
        api.get(`/api/members/${memberId}/subscription`)
      ]);

      if (profileRes.status === 'fulfilled') {
        setProfile(profileRes.value.data);
        setEditForm({
          fullName: profileRes.value.data.fullName || profileRes.value.data.FullName || '',
          email: profileRes.value.data.email || profileRes.value.data.Email || ''
        });
      }

      if (subRes.status === 'fulfilled') {
        const subData = subRes.value.data;
        const list = Array.isArray(subData) ? subData : subData ? [subData] : [];
        setActiveSubscriptions(list);

        const activeTierNames = list.map(item => getTierName(item));
        localStorage.setItem('activeTiers', JSON.stringify(activeTierNames));
      }
    } catch (err) {
      console.error("Error loading member data:", err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchProfileAndSubscription();
  }, [fetchProfileAndSubscription]);

  // Fetch All Tiers for Modal
  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await api.get('/api/membershiptiers/GetAllTiers');
        setTiers(res.data || []);
      } catch (err) {
        console.error("Failed to load tiers", err);
      }
    };
    fetchTiers();
  }, []);

  // Active Tier Checker
  const isTierActive = (tierId, tier) => {
    const targetName = getTierName(tier);
    return activeSubscriptions.some(sub => 
      (sub.tierId && sub.tierId === tierId) || 
      (sub.TierId && sub.TierId === tierId) || 
      (getTierName(sub) === targetName)
    );
  };

  // Upgrade Plan Handler
  const handleUpgradeTier = async (selectedTier) => {
    try {
      setUpgrading(true);
      const targetTierId = selectedTier.tierId || selectedTier.TierId || selectedTier.id;
      const targetAmount = selectedTier.price || selectedTier.Price || 0;

      await api.post(`/api/members/${memberId}/upgrade`, {
        tierId: targetTierId,
        amount: targetAmount
      });

      setShowUpgradeModal(false);
      fetchProfileAndSubscription();
    } catch (err) {
      console.error("Purchase failed", err);
    } finally {
      setUpgrading(false);
    }
  };

  // Profile Update Handler
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put(`/api/members/profile/${memberId}`, editForm);
      setShowEditModal(false);
      fetchProfileAndSubscription();
    } catch (err) {
      console.error("Failed to update profile", err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('memberId');
    localStorage.removeItem('activeTiers');
    navigate('/login');
  };

  if (loading) {
    return (
      <CContainer className="text-center py-5 my-5">
        <CSpinner color="primary" size="lg" />
        <p className="mt-3 fw-bold text-dark">Profile loading...</p>
      </CContainer>
    );
  }

  return (
    <div className="bg-light py-5 min-vh-100">
      <CContainer style={{ maxWidth: '1000px' }}>
        <CRow className="g-4">
          
          {/* Left Side: Profile Summary Card */}
          <CCol md={4}>
            <CCard className="border-0 shadow-sm rounded-4 text-center p-4">
              <CCardBody className="d-flex flex-column align-items-center">
                <CAvatar 
                  color="primary" 
                  textColor="white" 
                  size="xl" 
                  className="fw-bold fs-2 shadow-sm mb-3"
                  style={{ width: '80px', height: '80px' }}
                >
                  {profile?.fullName ? profile.fullName.charAt(0).toUpperCase() : 'U'}
                </CAvatar>

                <h5 className="fw-bold mb-1 text-dark">{profile?.fullName || profile?.FullName || 'N/A'}</h5>
                <p className="text-muted small mb-3">{profile?.email || profile?.Email || 'N/A'}</p>

                <div className="d-flex gap-2 mb-3">
                  <CBadge color={profile?.isActive || profile?.IsActive ? 'success' : 'danger'} className="px-3 py-2 rounded-pill">
                    {profile?.isActive || profile?.IsActive ? 'Active Account' : 'Inactive Account'}
                  </CBadge>
                </div>

                <CButton 
                  color="light" 
                  size="sm" 
                  className="w-100 fw-bold border mb-3 d-flex align-items-center justify-content-center gap-1"
                  onClick={() => setShowEditModal(true)}
                >
                  <Edit3 size={14} /> Edit Profile
                </CButton>

                <div className="w-100 border-top pt-3 text-start">
                  <small className="text-muted d-block mb-1">Total Spent:</small>
                  <h6 className="fw-bold text-success mb-3">₹{profile?.totalPaidAmount || profile?.TotalPaidAmount || 0}</h6>
                  
                  <CButton 
                    color="danger" 
                    variant="outline" 
                    className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          {/* Right Side: Detailed Subscriptions & Payments */}
          <CCol md={8}>
            
            {/* Active Plans Card */}
            <CCard className="border-0 shadow-sm rounded-4 mb-4">
              <CCardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                  <CreditCard size={18} className="text-primary" /> Active Subscriptions ({activeSubscriptions.length})
                </h6>
              <CButton 
  color="primary" 
  size="sm" 
  className="fw-bold d-flex align-items-center gap-1 cursor-pointer"
  onClick={() => navigate('/plans')}
>
  <ArrowUpRight size={16} /> Buy More Plans
</CButton>
              </CCardHeader>
              <CCardBody className="p-4">
                {activeSubscriptions.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {activeSubscriptions.map((sub, idx) => {
                      const tName = getTierName(sub);
                      const priceVal = sub.price ?? sub.Price ?? 0;

                      return (
                        <div 
                          key={idx} 
                          className="p-3 border rounded-3 bg-white d-flex align-items-start justify-content-between flex-wrap gap-2"
                        >
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <h6 className="fw-bold text-dark mb-0">{tName}</h6>
                              <CBadge color="success" className="px-2 py-1">Active</CBadge>
                            </div>

                            {renderCategoriesDisplay(sub)}

                            <small className="text-muted d-block mt-1">
                              Validity: <strong>{getValidityDisplay(sub)}</strong>
                            </small>
                          </div>

                          <div>
                            <span className="fw-bold text-primary fs-5">
                              {priceVal === 0 ? 'Free' : `₹${priceVal}`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-muted mb-2">No active subscription plan found.</p>
                   <CButton color="primary" size="sm" onClick={() => navigate('/plans')}>
  Explore Plans
</CButton>
                  </div>
                )}
              </CCardBody>
            </CCard>

            {/* Payment History Card */}
            <CCard className="border-0 shadow-sm rounded-4">
              <CCardHeader className="bg-white border-bottom py-3">
                <h6 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                  <Clock size={18} className="text-info" /> Recent Transactions
                </h6>
              </CCardHeader>
              <CCardBody className="p-0">
                {profile?.recentPayments && profile.recentPayments.length > 0 ? (
                  <CTable align="middle" responsive hover className="mb-0">
                    <CTableHead color="light">
                      <CTableRow>
                        <CTableHeaderCell>Date</CTableHeaderCell>
                        <CTableHeaderCell>Amount</CTableHeaderCell>
                        <CTableHeaderCell>Status</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {profile.recentPayments.map((pay, index) => (
                        <CTableRow key={index}>
                          <CTableDataCell>{new Date(pay.paymentDate || pay.PaymentDate).toLocaleDateString()}</CTableDataCell>
                          <CTableDataCell className="fw-bold">₹{pay.amount || pay.Amount}</CTableDataCell>
                          <CTableDataCell>
                            <CBadge color={(pay.status || pay.Status) === 'Success' ? 'success' : 'danger'}>
                              {pay.status || pay.Status}
                            </CBadge>
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                ) : (
                  <p className="text-muted text-center py-4 mb-0">No payment records found.</p>
                )}
              </CCardBody>
            </CCard>

          </CCol>
        </CRow>
      </CContainer>

      {/* Edit Profile Modal */}
      <CModal visible={showEditModal} onClose={() => setShowEditModal(false)}>
        <CModalHeader onClose={() => setShowEditModal(false)}>
          <CModalTitle className="fw-bold h5">Edit Profile</CModalTitle>
        </CModalHeader>
        <form onSubmit={handleUpdateProfile}>
          <CModalBody>
            <div className="mb-3">
              <CFormLabel className="fw-semibold">Full Name</CFormLabel>
              <CFormInput 
                type="text" 
                value={editForm.fullName} 
                onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                required 
              />
            </div>
            <div className="mb-3">
              <CFormLabel className="fw-semibold">Email Address</CFormLabel>
              <CFormInput 
                type="email" 
                value={editForm.email} 
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required 
                readOnly
              />
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setShowEditModal(false)}>Cancel</CButton>
            <CButton color="primary" type="submit" disabled={saving}>
              {saving ? <CSpinner size="sm" /> : 'Save Changes'}
            </CButton>
          </CModalFooter>
        </form>
      </CModal>

      {/* Buy / Upgrade Plans Modal */}
      <CModal
        size="lg"
        visible={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        centered
      >
        <CModalHeader onClose={() => setShowUpgradeModal(false)} className="border-bottom-0 pb-0 pt-3 px-4">
          <div>
            <CModalTitle className="fw-bold fs-5 text-dark mb-1">Available Plans</CModalTitle>
            <p className="text-muted small mb-0">Select any plan to activate alongside your current plans.</p>
          </div>
        </CModalHeader>

        <CModalBody className="p-4">
          <CRow className="g-3">
            {tiers.map((t, idx) => {
              const tId = t.tierId || t.TierId || t.id || idx;
              const tName = getTierName(t);
              const priceVal = t.price ?? t.Price ?? 0;

              // Dynamic Color Resolution
              const config = getTierStyle(t, idx);
              const isActive = isTierActive(tId, t);

              return (
                <CCol md={6} key={tId}>
                  <div
                    className="p-3 rounded-3 position-relative h-100 d-flex flex-column justify-content-between"
                    style={{
                      backgroundColor: isActive ? config.accentBg : '#FFFFFF',
                      border: `1.5px solid ${isActive ? config.color : '#E5E7EB'}`,
                      borderLeft: `5px solid ${config.color}`,
                      boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
                    }}
                  >
                    <div>
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                        <span className="fw-bold text-dark fs-6">{tName}</span>
                        {isActive && (
                          <CBadge
                            className="px-2 py-1 fw-medium d-flex align-items-center gap-1"
                            style={{
                              backgroundColor: config.color,
                              color: '#FFF',
                              fontSize: '10px',
                              borderRadius: '4px',
                            }}
                          >
                            <CheckCircle2 size={10} /> ACTIVE
                          </CBadge>
                        )}
                      </div>

                      {/* Render Category Details */}
                      {renderCategoriesDisplay(t)}
                    </div>

                    <div className="d-flex align-items-center justify-content-between pt-2 border-top mt-2">
                      <div className="d-flex align-items-baseline gap-1">
                        <span className="fw-bold text-dark fs-4">
                          {priceVal === 0 ? 'Free' : `₹${priceVal}`}
                        </span>
                        <span className="text-muted small">
                          / {getValidityDisplay(t)}
                        </span>
                      </div>

                      <CButton
                        size="sm"
                        className="px-3 py-1.5 fw-semibold border-0 rounded-2"
                        style={{
                          backgroundColor: isActive ? '#9CA3AF' : config.color,
                          color: '#FFFFFF',
                          fontSize: '13px',
                          cursor: isActive ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isActive || upgrading}
                        onClick={() => handleUpgradeTier(t)}
                      >
                        {isActive ? 'Active' : priceVal === 0 ? 'Select' : 'Buy Plan'}
                      </CButton>
                    </div>
                  </div>
                </CCol>
              );
            })}
          </CRow>
        </CModalBody>
      </CModal>

    </div>
  );
};

export default UserProfile;